/**
 * Virtual filesystem abstraction for document persistence
 * Uses IndexedDB with yaqatw-style path structure:
 * fs:/{projectName}/__documents__/{filename}/{dataType}
 *
 * This provides a path-based interface while IndexedDB handles actual storage.
 */

import { db } from "./db";
import { Cipher } from "./crypto/cipher";
import { useProjectStore } from "../store/projectStore";
import type { EntityScope } from "../types";

/**
 * Path to the projects registry (project names + metadata). Deliberately
 * exempt from the encryption below — it must stay readable before any
 * password has been entered, so the home screen can list projects.
 */
export const PROJECTS_REGISTRY_PATH = "/__projects__/index.json";

/**
 * Every other path is namespaced /{projectName}/... — pull that leading
 * segment out so reads/writes can be encrypted with the right project's key.
 */
function projectNameFromPath(path: string): string | null {
  const match = path.match(/^\/([^/]+)\//);
  return match ? match[1] : null;
}

/**
 * All document/highlight/settings/xml data is encrypted at rest with the
 * active project's key (see src/data/projects.ts for how that key is
 * derived from the project's password). Encryption is transparent to every
 * fs.ts consumer — it happens here, once, rather than at each call site.
 */
async function encryptForStorage(path: string, data: any): Promise<any> {
  if (path === PROJECTS_REGISTRY_PATH) return data;
  const pathProject = projectNameFromPath(path);
  const { projectName: activeName, projectKey } = useProjectStore.getState();
  if (!pathProject || pathProject !== activeName || !projectKey) {
    throw new Error(`No unlocked project key available to write "${path}".`);
  }
  return Cipher.encryptWithKey(JSON.stringify(data), projectKey);
}

async function decryptFromStorage(path: string, value: any): Promise<any> {
  if (value === null || value === undefined) return value;
  if (path === PROJECTS_REGISTRY_PATH) return value;
  const pathProject = projectNameFromPath(path);
  const { projectName: activeName, projectKey } = useProjectStore.getState();
  if (!pathProject || pathProject !== activeName || !projectKey) {
    throw new Error(`No unlocked project key available to read "${path}".`);
  }
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  return JSON.parse(await Cipher.decryptWithKey(bytes, projectKey));
}

/**
 * Confirm `key` can actually decrypt this project's stored data before it's
 * adopted as the active key. A wrong key (e.g. a registry `wrappedKey` that has
 * drifted from the key the documents were really encrypted with) fails AES-GCM
 * authentication on *every* at-rest file, so a single successful decrypt is
 * proof the key matches. Entering with a mismatched key silently corrupts the
 * project — data written afterwards is encrypted with a key the next unlock
 * can't reproduce — so callers use this to refuse instead (see
 * projects.ts's unlock/import paths).
 *
 * A project with nothing encrypted stored yet (freshly created, or a fresh
 * device before its documents have synced down) can't be checked and is
 * treated as valid — there's no data to contradict the key.
 */
export async function verifyProjectKey(projectName: string, key: CryptoKey): Promise<boolean> {
  const prefix = `/${projectName}/`;
  const paths = (await db.getKeys()).filter(
    (k) => k.startsWith(prefix) && k !== PROJECTS_REGISTRY_PATH
  );
  let sawCiphertext = false;
  for (const path of paths) {
    const item = await db.getItem(path);
    const value = item?.value;
    if (value === null || value === undefined) continue;
    let bytes: Uint8Array;
    try {
      bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
    } catch {
      continue; // not a byte payload (shouldn't happen under a project) — skip
    }
    // Too short to be a real cipher payload (header alone is 33 bytes) — not
    // something we can meaningfully test the key against.
    if (bytes.length < 33) continue;
    sawCiphertext = true;
    try {
      await Cipher.decryptWithKey(bytes, key);
      return true; // one good decrypt confirms the key; stop early
    } catch {
      // Wrong key, or this single file is corrupt — keep trying the others.
    }
  }
  // No testable ciphertext → nothing to contradict the key (valid).
  // Ciphertext existed but none of it decrypted → the key is wrong.
  return !sawCiphertext;
}

/**
 * Build a filesystem path from components. The middle segment is the document's
 * **fileId** (a lowercase slug), NOT its display name — so a document's folder
 * is stable and case-safe across the cloud round-trip (providers lowercase keys
 * on write). See src/store/tabsStore.ts.
 * Example: buildPath("my-project", "report", "document.json")
 * Returns: /my-project/__documents__/report/document.json
 */
export function buildPath(projectName: string, fileId: string, dataType: string): string {
  return `/${projectName}/__documents__/${fileId}/${dataType}`;
}

/**
 * Storage path of one yaqatw entity namespace's part in qda's own scope-aware
 * store (JSON envelopes, not raw XML — see src/entities/entityStore.ts). The
 * segment dispatches on the namespace's {@link EntityScope}:
 *   - "file"   → /{projectName}/__documents__/{fileId}/entities/{ns}.json
 *   - "shared" → /{projectName}/__shared__/{ns}.json  (project-wide, one copy)
 *
 * This is the single source of truth shared by hostBridge.ts (which stores every
 * part the yaqatw plugin writes via saveXmlPart) and the docx export/import
 * adapter (src/docx/customXmlSidecar.ts), so the two can never drift. The `ns`
 * URI is encodeURIComponent'd because it contains "/" and ":" characters.
 *
 * `fileId` is ignored for "shared" (pass anything). Both subtrees are mirrored
 * by cloud sync — see SYNC_SEGMENTS in src/cloud/qdaSync.ts.
 */
export const SHARED_SEGMENT = "__shared__";
export function buildEntityStorePath(
  scope: EntityScope,
  projectName: string,
  fileId: string,
  ns: string
): string {
  const file = `${encodeURIComponent(ns)}.json`;
  return scope === "shared"
    ? `/${projectName}/${SHARED_SEGMENT}/${file}`
    : buildPath(projectName, fileId, `entities/${file}`);
}

/**
 * Build working directory reference path (current open file)
 * Returns: /{projectName}/__working_directory__/reference.json
 */
export function buildWorkingRefPath(projectName: string): string {
  return `/${projectName}/__working_directory__/reference.json`;
}

/**
 * Parse a filesystem path into components
 */
export function parsePath(
  path: string
): { projectName: string; fileId: string; dataType: string } | null {
  const match = path.match(/^\/([^/]+)\/__documents__\/([^/]+)\/(.+)$/);
  if (!match) return null;
  return {
    projectName: match[1],
    fileId: match[2],
    dataType: match[3],
  };
}

/**
 * Write a file to the virtual filesystem. `modifiedTime` is normally omitted
 * (db.setItem stamps "now"); src/cloud/qdaSync.ts passes the cloud
 * provider's own modifiedTime after a download so the written copy's mtime
 * matches the remote's exactly — otherwise the next sync pass would see the
 * write-time "now" as newer than the remote and immediately re-upload an
 * unchanged file every cycle.
 */
export async function writeFile(path: string, data: any, modifiedTime?: string): Promise<void> {
  await db.setItem(path, await encryptForStorage(path, data), modifiedTime);
}

/**
 * Read a file from the virtual filesystem
 */
export async function readFile(path: string): Promise<any> {
  const item = await db.getItem(path);
  if (!item) return null;
  return decryptFromStorage(path, item.value);
}

/**
 * Delete a file from the virtual filesystem
 */
export async function unlink(path: string): Promise<void> {
  await db.removeItem(path);
}

/**
 * List all files in a directory path (prefix match)
 */
export async function readdir(dirPath: string): Promise<string[]> {
  // Ensure dirPath ends with /
  if (!dirPath.endsWith("/")) {
    dirPath += "/";
  }

  const allKeys = await db.getKeys();
  const matchingKeys = allKeys.filter((key) => key.startsWith(dirPath) && key !== dirPath);

  return matchingKeys;
}

/**
 * Check if a file exists
 */
export async function exists(path: string): Promise<boolean> {
  const allKeys = await db.getKeys();
  return allKeys.includes(path);
}

/**
 * Get file metadata (size, modified time)
 */
export async function stat(path: string): Promise<{ size: number; modified: string } | null> {
  const item = await db.getItem(path);
  if (!item) return null;

  const value = item.value;
  const size =
    typeof value === "string"
      ? value.length
      : value instanceof Uint8Array
        ? value.byteLength
        : JSON.stringify(value).length;

  return {
    size,
    modified: item.modifiedTime,
  };
}

/**
 * List all documents in a project
 */
export async function listDocuments(projectName: string): Promise<string[]> {
  const dirPath = `/${projectName}/__documents__/`;
  const files = await readdir(dirPath);

  // Extract unique document fileIds from paths like
  // /${projectName}/__documents__/${fileId}/... (the folder segment is the
  // fileId, not the display name — see buildPath).
  const fileIds = new Set<string>();
  for (const file of files) {
    const match = file.match(new RegExp(`^/${projectName}/__documents__/([^/]+)/`));
    if (match) {
      fileIds.add(match[1]);
    }
  }

  return Array.from(fileIds);
}

/**
 * List all files for a specific document (by fileId)
 */
export async function listDocumentFiles(projectName: string, fileId: string): Promise<string[]> {
  const dirPath = `/${projectName}/__documents__/${fileId}/`;
  return readdir(dirPath);
}

/**
 * Delete a complete document (all its files), by fileId
 */
export async function deleteDocument(projectName: string, fileId: string): Promise<void> {
  const files = await listDocumentFiles(projectName, fileId);
  for (const file of files) {
    await unlink(file);
  }
}

/**
 * Delete every stored file belonging to a project — documents, working
 * directory, xml parts, everything under /{projectName}/. Used when a project
 * is removed from this device (see projects.deleteProject). The project
 * registry itself lives outside this subtree and is updated separately.
 */
export async function deleteProjectData(projectName: string): Promise<void> {
  const prefix = `/${projectName}/`;
  const paths = (await db.getKeys()).filter((k) => k.startsWith(prefix));
  for (const path of paths) {
    await db.removeItem(path);
  }
}

/**
 * Export all filesystem data as a plain object for debugging
 */
export async function dump(): Promise<Record<string, any>> {
  const allKeys = await db.getKeys();
  const result: Record<string, any> = {};

  for (const key of allKeys) {
    const item = await db.getItem(key);
    if (item) {
      result[key] = {
        value: item.value,
        modifiedTime: item.modifiedTime,
      };
    }
  }

  return result;
}

export interface WorkingReference {
  /** fileId of the active tab, or null. */
  activeId: string | null;
  /** fileIds of every open tab, in order. */
  openIds: string[];
}

/**
 * Get the active document reference plus the full set of open tabs, all as
 * fileIds. Also tolerates a lone active id with no list by treating it as a
 * single-tab set.
 */
export async function getWorkingReference(projectName: string): Promise<WorkingReference> {
  const refPath = buildWorkingRefPath(projectName);
  const ref = await readFile(refPath);
  if (!ref) return { activeId: null, openIds: [] };
  const activeId = typeof ref.activeId === "string" && ref.activeId ? ref.activeId : null;
  let openIds = Array.isArray(ref.openIds)
    ? ref.openIds.filter((n: unknown): n is string => typeof n === "string")
    : [];
  if (openIds.length === 0 && activeId) openIds = [activeId];
  return { activeId, openIds };
}

/**
 * Set the active document reference and the full ordered set of open tabs (fileIds).
 */
export async function setWorkingReference(
  projectName: string,
  activeId: string | null,
  openIds: string[] = []
): Promise<void> {
  const refPath = buildWorkingRefPath(projectName);
  await writeFile(refPath, { activeId: activeId || "", openIds, timestamp: new Date().toISOString() });
}

// Export an fs-like namespace
export const fs = {
  writeFile,
  readFile,
  unlink,
  readdir,
  exists,
  stat,
  verifyProjectKey,
  deleteProjectData,
  listDocuments,
  listDocumentFiles,
  deleteDocument,
  dump,
  buildPath,
  buildEntityStorePath,
  parsePath,
  buildWorkingRefPath,
  getWorkingReference,
  setWorkingReference,
};

export default fs;
